<script lang="ts">
	import { t } from '$i18n/i18n'
	import { AllWhitelistData, type WhitelistCatalog } from './whitelistData'
	import { LotType } from '$types/enums'
	import HeadlessTable from '../organisms/HeadlessTable.svelte'
	import { writable, type Writable } from 'svelte/store'
	import { SvelteMap } from 'svelte/reactivity'
	import { getContext, hasContext, onMount, setContext } from 'svelte'
	import { createCol } from '$types/headlessTable'
	import { createRender } from 'svelte-headless-table'
	import ActiveTextCell from '../headlessTableCells/ActiveTextCell.svelte'
	import HtmlCell from '../headlessTableCells/HtmlCell.svelte'
	import { whitelistModalUtils } from './whitelistModalUtil'

	export let lotTypes: LotType[] = []
	let lotTypeTranslations = new SvelteMap<LotType, string>()

	function initializeTranslations() {
		lotTypes.forEach((lotType) =>
			lotTypeTranslations.set(
				<LotType>lotType,
				whitelistModalUtils.getLotTypeStringResource(lotType as LotType)
			)
		)
	}

	type WhitelistOverviewTableEntry = {
		lotType: LotType
		selectedDataText: string
	}

	// context stores
	let whitelistConfigData: Writable<Map<LotType, WhitelistCatalog[]>> = hasContext(
		'whitelistConfigData'
	)
		? getContext('whitelistConfigData')
		: writable(new Map<LotType, WhitelistCatalog[]>())
	let selectedData: Writable<AllWhitelistData> = hasContext('selectedWhitelistData')
		? getContext('selectedWhitelistData')
		: writable(new AllWhitelistData())
	let whitelistTransportConfigData: Writable<
		Map<LotType, { label: string; nodeSelector: string }[]>
	> = hasContext('whitelistTransportConfigData')
		? getContext('whitelistTransportConfigData')
		: writable(new Map<LotType, { label: string; nodeSelector: string }[]>())
	let selectedTransportData: Writable<Map<LotType, Map<string, string[]>>> = hasContext(
		'selectedWhitelistTransportData'
	)
		? getContext('selectedWhitelistTransportData')
		: writable(new Map<LotType, Map<string, string[]>>())

	// table data
	const tableData: Writable<WhitelistOverviewTableEntry[]> = setContext(
		'table-data',
		writable<WhitelistOverviewTableEntry[]>([])
	)
	const colData: Writable<ReturnType<typeof createCol>[]> = setContext(
		'table-cols',
		writable<ReturnType<typeof createCol>[]>([])
	)

	const NameCell = ({ row }: { row: { original: WhitelistOverviewTableEntry } }) =>
		createRender(ActiveTextCell, {
			text: lotTypeTranslations.get(row.original.lotType) ?? '',
			active: row.original.selectedDataText !== '-' // row is 'active' when we have properties mapped to the LotType
		})

	// when we have no active text, show it as inactive, if we have text it should be possibly scrollable
	const DataCell = ({ row }: { row: { original: WhitelistOverviewTableEntry } }) =>
		createRender(row.original.selectedDataText !== '-' ? HtmlCell : ActiveTextCell, {
			text: row.original.selectedDataText
		})
	const tablePlugins = {
		sort: { disable: true },
		filter: { exclude: true }
	}

	$colData.push(
		createCol($t('UI.whitelist.overview.table.name.header'), 'name', {
			plugins: tablePlugins,
			cell: NameCell
		})
	)
	$colData.push(
		createCol($t('UI.whitelist.overview.table.value.header'), 'selectedDataText', {
			plugins: tablePlugins,
			cell: DataCell
		})
	)

	onMount(() => {
		let tD: WhitelistOverviewTableEntry[] = []
		for (const [lotType, text] of $selectedData
			.getOverviewTextMappings(
				$whitelistConfigData,
				lotTypes,
				$whitelistTransportConfigData,
				$selectedTransportData
			)
			.entries()) {
			tD.push({ lotType, selectedDataText: text })
		}
		$tableData = tD
	})

	$: if (lotTypes) initializeTranslations()
</script>

<div class="whitelist-view-overview">
	<div>
		<span>{$t('UI.whitelist.modal.overview.header')}</span>
	</div>
	<div class="whitelist-view-overview-table scrollable">
		<HeadlessTable />
	</div>
</div>

<style lang="scss">
	@use '$styles/global';

	.whitelist-view-overview {
		width: 100%;

		& span {
			text-align: left;

			@include global.roboto-font(1rem, 500, 0.75rem, black);
		}
	}

	.whitelist-view-overview {
		& :global(.headless-table-header-cell) {
			--table-header-font-size: 0.75rem;
		}

		& :global(.headless-table-cell) {
			--table-cell-font-size: 0.75rem;
		}

		& :global(tr.headless-table-row > td.headless-table-cell:first-of-type),
		& :global(tr.headless-table-header > th.headless-table-header-cell:first-of-type) {
			width: 280px;
			max-width: 285px;
			display: flex;
			justify-content: flex-start;
		}

		& :global(tr.headless-table-row > td.headless-table-cell:last-of-type),
		& :global(tr.headless-table-header > th.headless-table-header-cell:last-of-type) {
			width: 100%;
		}

		& :global(tr.headless-table-header > th.headless-table-header-cell:last-of-type > div) {
			display: flex;
			justify-content: flex-start;
		}
	}

	.whitelist-view-overview-table {
		overflow-y: auto;
		max-height: 375px;
		margin-top: 22px;
	}
</style>
